<template>
  <div class="d-flex flex-column gap-4">
    <div class="card shadow-sm border border-gray-200">
      <div class="card-body py-4 px-4">
        <h6 class="fw-bold mb-3">Autorização</h6>
        <div class="row">
          <div class="col-md-4">
            <label class="fs-6 fw-bold mb-2">Número da Autorização</label>
            <input v-model="form.authorization_number"
                   type="text"
                   class="form-control form-control-solid"
                   maxlength="100"
                   placeholder="Número da autorização">
          </div>
          <div class="col-md-4">
            <label class="fs-6 fw-bold mb-2">Senha</label>
            <input v-model="form.authorization_password"
                   type="text"
                   class="form-control form-control-solid"
                   maxlength="50"
                   placeholder="Senha da autorização">
          </div>
          <div class="col-md-4">
            <label class="fs-6 fw-bold mb-2">Data de Validade</label>
            <input
              type="date"
              class="form-control form-control-solid"
              v-model="form.authorization_validity"
              placeholder="DD/MM/YYYY"
            />
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-4">
            <label class="fs-6 fw-bold mb-2">Data do atendimento</label>
            <input
              type="date"
              class="form-control form-control-solid"
              v-model="form.attendance_date"
              placeholder="DD/MM/YYYY"
            />
            <small class="text-muted">Opcional. Usada na guia e faturamento quando informada.</small>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border border-gray-200">
      <div class="card-body py-4 px-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="fw-bold mb-0">Procedimentos / Exames</h6>
          <button type="button" class="btn btn-sm btn-primary" @click="$emit('add-procedure')">
            <i class="bi bi-plus-lg"></i> Adicionar Procedimento
          </button>
        </div>

        <div v-if="procedures && procedures.length > 0" class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead class="table-light">
              <tr>
                <th>Código</th>
                <th>Tabela</th>
                <th>Descrição</th>
                <th>Qtd</th>
                <th>Valor Unit.</th>
                <th>Repasse unit.</th>
                <th>Total</th>
                <th class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(procedure, index) in procedures" :key="index">
                <td>{{ procedure.code }}</td>
                <td>{{ procedure.table_code }}</td>
                <td>{{ procedure.description }}</td>
                <td>{{ procedure.quantity }}</td>
                <td>R$ {{ formatCurrency(procedure.unit_value) }}</td>
                <td>R$ {{ formatCurrency(procedure.unit_transfer_value ?? 0) }}</td>
                <td>R$ {{ formatCurrency((procedure.unit_value || 0) * (procedure.quantity || 1) * (procedure.reduction_factor || 1)) }}</td>
                <td class="text-center">
                  <button type="button" class="btn btn-sm btn-icon btn-light-primary me-2"
                          @click="$emit('edit-procedure', index)" title="Editar">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-icon btn-light-danger"
                          @click="$emit('remove-procedure', index)" title="Remover">
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="procedures.length > 0" class="table-light">
              <tr>
                <td colspan="6" class="text-end fw-bold">Total:</td>
                <td class="fw-bold">R$ {{ formatCurrency(totalProceduresValue) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="alert alert-info mb-0">
          <i class="bi bi-info-circle me-2"></i>
          Nenhum procedimento adicionado. Clique em "Adicionar Procedimento" para incluir exames na guia.
        </div>
      </div>
    </div>

    <div class="card shadow-sm border border-gray-200">
      <div class="card-body py-4 px-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="fw-bold mb-0">Outras Despesas</h6>
          <button type="button" class="btn btn-sm btn-primary" @click="$emit('add-expense')">
            <i class="bi bi-plus-lg"></i> Adicionar Despesa
          </button>
        </div>

        <div v-if="otherExpenses && otherExpenses.length > 0" class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead class="table-light">
              <tr>
                <th>Tipo</th>
                <th>Código</th>
                <th>Descrição</th>
                <th>Data</th>
                <th>Qtd</th>
                <th>Unidade</th>
                <th>Valor Unit.</th>
                <th>Total</th>
                <th class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(expense, index) in otherExpenses" :key="index">
                <td>{{ getExpenseTypeLabel(expense.expense_code) }}</td>
                <td>{{ expense.item_code }}</td>
                <td>{{ expense.item_description }}</td>
                <td>{{ formatDateToBR(expense.execution_date) }}</td>
                <td>{{ expense.quantity }}</td>
                <td>{{ expense.unit_measure }}</td>
                <td>R$ {{ formatCurrency(expense.unit_value) }}</td>
                <td>R$ {{ formatCurrency((expense.unit_value || 0) * (expense.quantity || 0)) }}</td>
                <td class="text-center">
                  <button type="button" class="btn btn-sm btn-icon btn-light-primary me-2"
                          @click="$emit('edit-expense', index)" title="Editar">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-icon btn-light-danger"
                          @click="$emit('remove-expense', index)" title="Remover">
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="otherExpenses.length > 0" class="table-light">
              <tr>
                <td colspan="7" class="text-end fw-bold">Total:</td>
                <td class="fw-bold">R$ {{ formatCurrency(totalExpensesValue) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="alert alert-info mb-0">
          <i class="bi bi-info-circle me-2"></i>
          Nenhuma despesa adicionada. Clique em "Adicionar Despesa" para incluir.
        </div>
      </div>
    </div>

    <div class="card shadow-sm border border-gray-200">
      <div class="card-body py-4 px-4">
        <h6 class="fw-bold mb-3">Anexos</h6>
        <div class="row g-2 align-items-end">
          <div class="col-md-6">
            <label class="fs-6 fw-bold mb-2">Selecionar arquivos</label>
            <input
              type="file"
              class="form-control form-control-solid"
              multiple
              @change="onAttachmentsSelect"
              accept=".pdf,image/*,.doc,.docx,.txt"
            >
          </div>
        </div>
        <div v-if="attachments.length" class="mt-2">
          <table class="table table-sm table-borderless align-middle mb-0">
            <thead>
              <tr class="text-muted fw-bold small">
                <th>Nome</th>
                <th class="text-end">Tamanho</th>
                <th class="text-center" style="width: 60px;">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(file, index) in attachments" :key="index">
                <td class="small">
                  <i class="bi bi-paperclip me-1"></i>{{ file.name }}
                </td>
                <td class="text-end small">
                  {{ formatFileSize(file.size) }}
                </td>
                <td class="text-center">
                  <button
                    type="button"
                    class="btn btn-sm btn-icon btn-light-danger border border-gray-400"
                    title="Remover"
                    @click="removeAttachment(index)"
                  >
                    <i class="bi bi-x-lg"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="mt-2">
          <small class="text-muted">
            Nenhum anexo adicionado. Use o campo acima para anexar solicitação, laudos, etc.
          </small>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border border-gray-200">
      <div class="card-body py-4 px-4">
        <label class="fs-6 fw-bold mb-2">Observações</label>
        <textarea
          v-model="form.observations"
          class="form-control form-control-solid"
          rows="6"
          placeholder="Observações sobre a guia (opcional)"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TissSpSadtGuideForm',
  data() {
    return {
      attachments: [],
    };
  },
  props: {
    form: { type: Object, required: true },
    procedures: { type: Array, default: () => [] },
    otherExpenses: { type: Array, default: () => [] },
    totalProceduresValue: { type: Number, default: 0 },
    totalExpensesValue: { type: Number, default: 0 },
    formatCurrency: { type: Function, required: true },
    formatDateToBR: { type: Function, required: true },
    getExpenseTypeLabel: { type: Function, default: () => '' },
  },
  methods: {
    onAttachmentsSelect(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) {
        return;
      }
      this.attachments = [...this.attachments, ...files];
      this.$emit('update-attachments', this.attachments);
    },
    removeAttachment(index) {
      this.attachments.splice(index, 1);
      this.$emit('update-attachments', this.attachments);
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '-';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      let value = bytes;
      while (value >= 1024 && i < sizes.length - 1) {
        value /= 1024;
        i++;
      }
      return `${value.toFixed(1)} ${sizes[i]}`;
    },
  },
};
</script>
